import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { prettyJson } from '@/lib/format';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export function CopyButton({ value, className, label = t('Copy') }: { value: string; className?: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground',
        className,
      )}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </button>
  );
}

export function CodeBlock({
  value,
  className,
  maxHeight = 'max-h-96',
  showCopy,
}: {
  value: string;
  className?: string;
  maxHeight?: string;
  /** Always show the copy button (default: only on hover). */
  showCopy?: boolean;
}) {
  return (
    <div className={cn('group relative', className)}>
      <pre
        className={cn(
          'overflow-auto rounded-lg border bg-muted/50 p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words',
          showCopy && 'pr-10',
          maxHeight,
        )}
      >
        {value}
      </pre>
      <CopyButton
        value={value}
        className={cn('absolute top-1.5 right-1.5 bg-card shadow-xs', !showCopy && 'opacity-0 group-hover:opacity-100')}
      />
    </div>
  );
}

export function JsonBlock({ value, className, maxHeight }: { value: unknown; className?: string; maxHeight?: string }) {
  return <CodeBlock value={prettyJson(value)} className={className} maxHeight={maxHeight} />;
}
